import type { EditorListing, PriceTier, Unit } from 'src/common/types/api/listings';
import {
  CANCELATION_ANTICIPATION_FIELD,
  CANCELATION_REFUND_FIELD,
  CANCELATION_TERMS_FIELD,
  MAXIMUM_HOURS_DEFAULT,
  MAXIMUM_MULTIDAY_DEFAULT,
  DEPOSIT_FIELD,
  MAXIMUM_HOURS_FIELD,
  MAXIMUM_MULTIDAY_FIELD,
} from 'src/admin/listing-editor/constants';

export type PriceOptionData = {
  rate: number;
  duration: number;
  total?: number;
};

export type PriceFormData = {
  [CANCELATION_ANTICIPATION_FIELD]: number;
  [CANCELATION_REFUND_FIELD]: number;
  [DEPOSIT_FIELD]: number;
  hour?: PriceOptionData[];
  [MAXIMUM_HOURS_FIELD]?: number;
  multiday?: PriceOptionData[];
  [MAXIMUM_MULTIDAY_FIELD]?: number;
  [CANCELATION_TERMS_FIELD]: string;
  multidayUnit: Unit;
};

export const priceMapper = (tier: PriceTier, keepRate: boolean): PriceOptionData => {
  const rate = keepRate ? tier.value : 0;
  return {
    rate,
    duration: tier.fixed_quantity || tier.min_units_quantity,
    total: rate * (tier.min_units_quantity || 1),
  };
};

export const initialPriceOptionValue: PriceOptionData = { rate: 0, duration: 0, total: 0 };

export const multidayUnits = ['day', 'night'];

export type PriceOptionUnit = 'hour' | 'multiday';

export const filterPricingOptions = (
  { rate = 0, duration = 0 }: PriceOptionData,
): boolean => (rate + duration) > 0;

export const getListingPrices = ({ price_tiers }: { price_tiers?: PriceTier[] }): {
  hour: PriceTier[];
  multiday: PriceTier[];
} => ({
  hour: price_tiers?.filter(({ unit }) => unit === 'hour'),
  multiday: price_tiers?.filter(({ unit }) => multidayUnits.includes(unit)),
});

export const mapListingToInitialData = (
  listing: EditorListing,
  isImpersonatedSession: boolean = false,
): PriceFormData => {
  const keepRate = isImpersonatedSession || listing.currency === listing.payout_currency;
  const listingPrices = getListingPrices(listing);
  const initialValues: PriceFormData = {
    [CANCELATION_ANTICIPATION_FIELD]: listing[CANCELATION_ANTICIPATION_FIELD] ?? 0,
    [CANCELATION_REFUND_FIELD]: listing[CANCELATION_REFUND_FIELD] ?? 0,
    [DEPOSIT_FIELD]: listing.deposit ?? 0,
    hour: listingPrices.hour?.length
      ? listingPrices.hour.map((tier) => priceMapper(tier, keepRate))
      : [initialPriceOptionValue],
    [MAXIMUM_HOURS_FIELD]: listing[MAXIMUM_HOURS_FIELD] ?? MAXIMUM_HOURS_DEFAULT,
    multiday: listingPrices.multiday?.length
      ? listingPrices.multiday.map((tier) => priceMapper(tier, keepRate))
      : [initialPriceOptionValue],
    [MAXIMUM_MULTIDAY_FIELD]: listing[MAXIMUM_MULTIDAY_FIELD] ?? MAXIMUM_MULTIDAY_DEFAULT,
    [CANCELATION_TERMS_FIELD]: listing[CANCELATION_TERMS_FIELD],
    multidayUnit: (listingPrices.multiday?.[0]?.unit || 'day') as Unit,
  };

  return initialValues;
};
